import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import SwitchIcon from '@mui/icons-material/SyncAlt';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { Link as NavLink } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import { useDebouncedCallback } from 'use-debounce/lib';

import {
  apigQuery,
  spProductPricingEntity,
  useGetAllBusinessProductsQuery,
  useGetBusinessNumbers2Query,
  useGetBusinessTranchesQuery,
} from '../../api/apigQuery';
import { PopoverGeneric } from '../../common/PopoverGeneric';
import { loadableEnum } from '../../common/useMakePHPRequest';
import {
  trackAddBoostSetMGMT,
  trackAddDirectoryExternalNumber,
  trackAddExternalNumber,
  trackAddNumber,
  trackAddPort,
  trackEditNumber,
} from '../../common/userTracking';
import ReportIcon from '../../images/report-icon.svg';
import { IUserAtom, Role, user } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { AddExtModal } from '../components/modals/AddExtModal';
import { AddNumberModal } from '../components/modals/AddNumberModal';
import { AddPortModal } from '../components/modals/AddPortModal';
import BoostSetMgmtModal from '../components/modals/BoostSetMgmtModal';
import { ConferenceModal } from '../components/modals/ConferenceModal';
import { ConfirmDeleteNumberModal } from '../components/modals/ConfirmDeleteNumeberModal';
import { DeleteNumberModal } from '../components/modals/DeleteNumberModal';
import { EditNumberModal } from '../components/modals/EditNumberModal';
import HistoryExportModal from '../components/modals/HistoryExportModal';
import { MakePhoneCallModal } from '../components/modals/MakePhoneCallModal';
import { ReplaceNumberModal } from '../components/modals/ReplaceNumberModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { QueueEditDialog } from '../modules/Manage/pages/queue-edit-dialog/QueueEditDialog';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { IvrSettings } from '../subpages/IvrSettings';
import { RingGroupSettings } from '../subpages/RingGroupSettings';
// import { DashboardTourExtraSpotlights } from './dashboardTour/DashboardTourExtraSpotlights';

import type { AppDispatch } from '../store';

interface NumberPriceAndCycle {
  price: number;
  billingCycle: number;
}
interface BusPhoneLineData {
  localLine: spProductPricingEntity;
  tollFreeLine: spProductPricingEntity;
}

export const getPriceAndCycle = (
  productData?: BusPhoneLineData,
  numType?: string
): NumberPriceAndCycle => {
  const DEFAULT_NUMBER_PRICE = 5;
  const DEFAULT_BILLING_CYCLE = 1;
  if (!productData) {
    return {
      price: DEFAULT_NUMBER_PRICE,
      billingCycle: DEFAULT_BILLING_CYCLE,
    };
  }
  if (!numType) {
    return {
      price: productData.localLine?.proratedPrice ?? DEFAULT_NUMBER_PRICE,
      billingCycle:
        productData.localLine?.productBillingCycle ?? DEFAULT_BILLING_CYCLE,
    };
  }
  let type = '';
  switch (numType?.toLowerCase()) {
    case 'tollfree':
      type = 'tollFreeLine';
      break;
    case 'tollfreetrackingline':
      type = 'tollFreeLine';
      break;
    case 'local':
      type = 'localLine';
      break;
    case 'localtrackingline':
      type = 'localLine';
      break;
  }

  return {
    price: productData?.[type]?.proratedPrice ?? DEFAULT_NUMBER_PRICE,
    billingCycle:
      productData?.[type]?.productBillingCycle ?? DEFAULT_BILLING_CYCLE,
  };
};

const useStyles = makeStyles((theme: Theme & { custom: any }) => ({
  skeleton: {
    backgroundColor: theme.palette.mode === 'dark' ? 'grey' : 'lightgrey',
    height: '6em',
    margin: 10,
  },
  buttonContainedRed: {
    color: theme.custom.button.delete.contrastText,
    backgroundColor: theme?.custom?.button?.delete?.main,
    marginLeft: theme.spacing(1), // space between fields
    '&:hover': {
      backgroundColor: theme.custom.button.delete.dark,
    },
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  button: {
    marginRight: theme.spacing(1),
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
  },
  actionButton: {
    // marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
  },
  chip: {
    backgroundColor: '#4caf50',
  },
  statHeader: {
    fontSize: '0.9rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    color:
      theme.palette.mode === 'light'
        ? 'rgb(107, 119, 140)'
        : 'rgb(145, 158, 171)',
  },
  statHeaderCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  numberScoreContainer: {
    padding: '0px 8px',
  },
  greatNumberScore: {
    color: 'rgb(0,205,129)',
  },
  goodNumberScore: {
    color: 'rgb(153,195,129)',
  },
  averageNumberScore: {
    color: 'rgb(255,113,110)',
  },
  poorNumberScore: {
    color: 'rgb(225,53,50)',
  },
  cautionNumberScore: {
    color:
      theme.palette.mode === 'light'
        ? 'rgb(107, 119, 140)'
        : 'rgb(145, 158, 171)',
  },
  numberScore: {
    fontSize: '2.5rem',
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  numberScore2: {
    fontSize: '1.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    color:
      theme.palette.mode === 'dark' ? 'rgb(250, 250, 250)' : 'rgb(0, 0, 0)',
  },
  statHeaderContainer: {
    display: 'flex',
  },
  subtext: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'black',
  },
  statHeaderContainer2: {
    alignSelf: 'center',
    margin: '0px 8px',
    // marginLeft: '32px'
  },
  typeColumn: {
    minWidth: '115px !important',
    maxWidth: '115px !important',
    '&.MuiDataGrid-cell': {
      wordBreak: 'keep-all !important',
    },
  },
  dropdownOverflow: {
    overflow: 'visible !important',
    '.dropdown': {
      zIndex: 10,
    },
  },
  actionsDropdown: {
    borderRadius: 50,
    height: '40px',
    width: '40px',
  },
  dropdownToggle: {
    background: 'transparent !important',
    border: 0,
    '&:after': {
      color: '#000 !important',
      margin: '0 !important',
    },
  },
  dropdownButton: {
    borderRadius: '0 !important',
    fontSize: 14,
    color: '#000',
    width: '100%',
    textAlign: 'left',
    padding: '5px',
    '& .MuiIconButton-label': {
      display: 'block !important',
    },
  },
  search: {
    marginTop: '5px', // zero out material-ui dense
    marginBottom: 0, // zero out material-ui dense
    marginRight: '1vw', // space for loading icon
  },
}));
const ReportLogo = (props: { className?: string }) => {
  return (
    <img
      alt="Report Logo"
      style={{ height: '24px', width: '24px' }}
      src={ReportIcon}
    />
  );
};

export function ManageNumbers() {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [newModal, handleNewModal] = useState(false);
  const [portModal, handlePortModal] = useState(false);
  const [boostSetModal, handleBoostSetModal] = useState(false);
  const [extModal, handleExtModal] = useState(false);
  const [replaceNumberModal, handleReplaceNumberModal] = useState(false);
  const [isDirectoryNumber, setIsDirectoryNumber] = useState(false);
  const [deleteModal, handleDeleteModal] = useState(false);
  const [selectedToggle, setSelectedToggle] = useState(false);
  const [editModal, handleEditModal] = useState(false);
  const [makePhoneCallModal, handleCallModal] = useState(false);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const [deleteStatus, setDeleteStatus] = useState(loadableEnum.EMPTY);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setGridSortModel] = React.useState<GridSortModel>([
    { field: 'id', sort: 'desc' },
  ]);
  const dispatch = useDispatch<AppDispatch>();

  const {
    role,
    canAddPhoneNumbers,
  } = permissionsLoadable.contents as IPermissions;
  const businessUsers = useRecoilValueLoadable<
    | {
        response?: {
          data?: {
            success?: boolean;
            result?: {
              userid: number;
              fname: string;
              lname: string;
            }[];
          };
        };
      }
    | undefined
  >(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
      waitForPayload: userAtomLoadable.state !== 'hasValue',
    })
  );

  const [
    businessSMSRulesCashed,
    businessSMSRulesLoadable,
    setRefetchToggleForSMSRules,
  ] = makeApiRequestCached<
    | {
        nameLookup: Record<string, IBusinessSMS>;
        /** businessSMSId: string, name: stirng */
        smsIdLookup: Record<string, string>;
      }
    | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_SMS_BY_BUSINESS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_SMS_BY_BUSINESS,
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
      waitForPayload: userAtomLoadable.state !== 'hasValue',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.reduce(
        (
          acc: {
            nameLookup: Record<string, IBusinessSMS>;
            /** businessSMSId: string, name: stirng */
            smsIdLookup: Record<string, string>;
          },
          item: IBusinessSMS
        ) => {
          if (!acc.nameLookup) acc.nameLookup = {};
          acc.nameLookup[item.name] = item;

          if (!acc.smsIdLookup) acc.smsIdLookup = {};
          acc.smsIdLookup[item.businessSMSId] = item.name;

          return acc;
        },
        {
          nameLookup: {} as Record<string, IBusinessSMS>,
          smsIdLookup: {} as Record<string, string>,
        }
      ) ?? cached.current
  );

  const usernameLookup = useMemo(() => {
    return businessUsers.state === 'hasValue'
      ? businessUsers.contents?.response?.data?.result?.reduce(
          (acc, user) => {
            acc[user.userid] =
              user.fname === undefined ? 'N/A' : `${user.fname} ${user.lname}`;
            return acc;
          },
          /** userid: string, name: string */
          {} as Record<string, string>
        )
      : undefined;
  }, [businessUsers.contents]);

  const {
    data: businessNumbers,
    // refetch: _refetchBusinessNumbers,
    isFetching: isBusinessNumbersFetching,
    isSuccess: isBusinessNumbersSuccess,
  } = useGetBusinessNumbers2Query(
    {
      userSettings: {
        userId: userAtomContents?.userid,
        userid: userAtomContents?.userid,
        businessId: userAtomContents?.businessid,
        businessid: userAtomContents?.businessid,
        apikey: userAtomContents?.apikey,
      },
      skip: (page - 1) * pageSize,
      limit: pageSize,
      smsIdLookup: businessSMSRulesCashed.current?.smsIdLookup,
      usernameLookup: usernameLookup,
      searchText: search,
      sort: sortModel[0]?.field,
      sortDirection: sortModel[0]?.sort,
    },
    {
      skip:
        userAtomLoadable.state !== 'hasValue' ||
        businessSMSRulesLoadable.state !== 'hasValue' ||
        businessUsers.state !== 'hasValue',
    }
  );

  /**
   * Reset state to first visiting
   * scenarios to reset: page change and search
   */
  function resetView() {
    // move to first page to refresh count
    setPage(1);
  }

  /**
   * Refresh data since pagemap/count may have of changed
   */
  function refetchBusinessNumbers() {
    resetView();
    // clear cache for all pages in background as macrotask
    setTimeout(() =>
      dispatch(apigQuery.util.invalidateTags(['getBusinessNumbers2']))
    );

    // /**
    //  * Awaited typescript keyword not working with babel
    //  * @see https://stackoverflow.com/questions/48011353/how-to-unwrap-the-type-of-a-promise
    //  * @todo use Awaited instead after upgrading to vite
    //  */
    // type Await<T> = T extends PromiseLike<infer U> ? U : T;
    // const prevPage = page;
    // // create macrotask to wait for react to finish rerendering state changes
    // // note: promises are microtasks however setTimeout is a macrotask
    // // get return type and unwrap promise
    // return new Promise<Await<ReturnType<typeof _refetchBusinessNumbers>>>(
    //   (resolve, reject) =>
    //     setTimeout(async () => {
    //       try {
    //         // invalidate other pages
    //         dispatch(apigQuery.util.invalidateTags(['getBusinessNumbers2']));
    //         const refetchResult = await _refetchBusinessNumbers(); // refetch first page
    //         setPage(prevPage); // move back to previous page (auto-fetch)
    //         resolve(refetchResult);
    //       } catch (exception) {
    //         // must reject explicitly since inside of setTimeout and
    //         // exception will not bubble up to promise to trigger termination
    //         reject(exception);
    //       }
    //     }, 0)
    // );
  }

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
    resetView();
  };

  const {
    currentData: trancheRes,
    isLoading,
    refetch,
    isFetching,
  } = useGetBusinessTranchesQuery(
    {
      userSettings: {
        userId: userAtomContents?.userid,
        userid: userAtomContents?.userid,
        businessId: userAtomContents?.businessid,
        businessid: userAtomContents?.businessid,
        apikey: userAtomContents?.apikey,
      },
    },
    {
      skip: userAtomLoadable.state !== 'hasValue',
    }
  );

  const { currentData: busProducts } = useGetAllBusinessProductsQuery(
    {
      userSettings: {
        userId: userAtomContents?.userid,
        userid: userAtomContents?.userid,
        businessId: userAtomContents?.businessid,
        businessid: userAtomContents?.businessid,
        apikey: userAtomContents?.apikey,
      },
    },
    {
      skip: userAtomLoadable.state !== 'hasValue',
    }
  );
  const productData = useMemo(() => {
    const d = busProducts ?? [];
    return {
      localLine: d?.filter((p) => p?.product === 'localtrackingline')?.[0],
      tollFreeLine: d?.filter(
        (p) => p?.product === 'tollfreetrackingline'
      )?.[0],
    };
  }, [busProducts]);

  const boostSetPricing = useMemo(() => {
    const d = busProducts ?? [];
    return d?.filter((p) => p.product === 'connectionboostprivate')?.[0];
  }, [busProducts]);

  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const conferencesCall = {
    call: methodNameEnum.GET_BUSINESS_CONFERENCES,
  };
  const getBusinessConferences = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_CONFERENCES,
      payloadJsonString: JSON.stringify(conferencesCall),
      waitForPayload: userAtomLoadable.state !== 'hasValue',
    })
  );

  const numbersSpamLikelyScore = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_NUMBER_SPAM_LIKELY_SCORE}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'getNumberSpamLikelyScore',
      }),
      waitForPayload: userAtomLoadable.state !== 'hasValue',
    })
  );

  const [_, routingRulesLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_ROUTING_RULES,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_ROUTING_RULES,
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
      waitForPayload: !userAtomContents?.businessid,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  const routingRulesEntityMap = useMemo(() => {
    const routingMap = {
      All: {},
      Agent: {},
      Conference: {},
      Group: {},
      IVR: {},
      Number: {} as { entity?: Record<string, any> },
      Queue: {},
      'Boost Set': {},
      unallocated: {},
    };
    const routingRules = routingRulesLoadable?.contents?.response?.data?.result;
    if (routingRules) {
      try {
        const parsedMap = routingRules.map((rule) =>
          JSON.parse(rule?.JSON ?? '{}')
        );
        const numbersMap: {
          [key: string]: any[];
        } = {
          Group: [],
          IVR: [],
          Agent: [],
          Conference: [],
          Queue: [],
          'Boost Set': [],
          unallocated: [],
        };
        businessNumbers?.result?.forEach((businessNumber) => {
          if (Array.isArray(numbersMap[businessNumber.type])) {
            numbersMap[businessNumber.type].push(businessNumber);
          }
        });
        parsedMap.forEach((rule) => {
          Object.keys(routingMap).forEach((key) => {
            if (rule?.numbers?.[0]?.[key]) {
              rule.numbers[0][key].forEach((num) => {
                if (key === 'All') {
                  if (isBusinessNumbersSuccess) {
                    switch (num) {
                      case 'localPresence':
                        const lpNumbers = numbersMap['Boost Set'];
                        lpNumbers.forEach((lpNumber) => {
                          const id = lpNumber.Numberid;
                          if (!routingMap['Boost Set'][id]) {
                            routingMap['Boost Set'][id] = 1;
                          } else {
                            routingMap['Boost Set'][id] += 1;
                          }
                        });
                        break;
                      case 'localPresenceLite':
                        const lpliteNumbers = numbersMap.unallocated.filter(
                          (unallocatedNum) => unallocatedNum.localpresencelite
                        );
                        lpliteNumbers.forEach((lpliteNumber) => {
                          const id = lpliteNumber.Numberid;
                          if (!routingMap.unallocated[id]) {
                            routingMap.unallocated[id] = 1;
                          } else {
                            routingMap.unallocated[id] += 1;
                          }
                        });
                        break;
                      case 'group':
                        const groupNumbers = numbersMap.Group;
                        groupNumbers.forEach((groupNumber) => {
                          const id = groupNumber.Numberid;
                          if (!routingMap.Group[id]) {
                            routingMap.Group[id] = 1;
                          } else {
                            routingMap.Group[id] += 1;
                          }
                        });
                        break;
                      case 'ivr':
                        const ivrNumbers = numbersMap.IVR;
                        ivrNumbers.forEach((number) => {
                          const id = number.Numberid;
                          if (!routingMap.IVR[id]) {
                            routingMap.IVR[id] = 1;
                          } else {
                            routingMap.IVR[id] += 1;
                          }
                        });
                        break;
                      case 'agent':
                        const agentNumbers = numbersMap.Agent;
                        agentNumbers.forEach((number) => {
                          const id = number.Numberid;
                          if (!routingMap.Agent[id]) {
                            routingMap.Agent[id] = 1;
                          } else {
                            routingMap.Agent[id] += 1;
                          }
                        });
                        break;
                      case 'conference':
                        const conferenceNumbers = numbersMap.Conference;
                        conferenceNumbers.forEach((number) => {
                          const id = number.Numberid;
                          if (!routingMap.Conference[id]) {
                            routingMap.Conference[id] = 1;
                          } else {
                            routingMap.Conference[id] += 1;
                          }
                        });
                        break;
                      case 'queue':
                        const queueNumbers = numbersMap.Queue;
                        queueNumbers.forEach((number) => {
                          const id = number.Numberid;
                          if (!routingMap.Queue[id]) {
                            routingMap.Queue[id] = 1;
                          } else {
                            routingMap.Queue[id] += 1;
                          }
                        });
                        break;
                    }
                  }
                } else {
                  if (!routingMap[key].entity) {
                    routingMap[key].entity = {};
                  }
                  if (!routingMap[key].entity[num]) {
                    routingMap[key].entity[num] = 1;
                  } else {
                    routingMap[key].entity[num] += 1;
                  }
                }
              });
            }
          });
        });
      } catch (e) {
        //
      }
    }
    return routingMap;
  }, [routingRulesLoadable, businessNumbers, isBusinessNumbersSuccess]);

  const conferencesCached = useRef([]);
  conferencesCached.current = useMemo(
    () =>
      getBusinessConferences?.state === 'hasValue'
        ? getBusinessConferences.contents?.response?.data?.result?.map((o) => ({
            ...o,
            id: o.conferenceid,
          })) ?? []
        : conferencesCached.current,
    [getBusinessConferences]
  );

  const rowsCached = useRef<any[]>([]);
  rowsCached.current = useMemo(
    () =>
      businessNumbers && isBusinessNumbersSuccess
        ? businessNumbers?.result?.map((n) => ({
            ...n,
            id: n.Numberid,
            routingRules:
              (routingRulesEntityMap[n.type]?.[n.Numberid!] ?? 0) +
              (routingRulesEntityMap[n.type]?.entity?.[n.destinationid] ?? 0) +
              (routingRulesEntityMap?.Number?.entity?.[n.DID] ?? 0),
          })) ?? []
        : rowsCached.current,
    [businessNumbers, routingRulesEntityMap, isBusinessNumbersSuccess]
  );

  const { treatment } = useFeatureFlag('SWARM-70-csv-export');
  const isCsvExportEnabled = useMemo(
    () => treatment === 'control' || treatment === 'on',
    [treatment]
  );

  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [csvProps, setCsvProps] = useState<any>({});
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [deleteGroupId, setDeleteGroupId] = useState<string | undefined>('');
  const [reportGroupId, setReportGroupId] = useState<string | undefined>('');
  const [rowForEdit, setRowForEdit] = useState<Record<string, any>>({});
  const [rowForDelete, setRowForDelete] = useState<Record<string, any>>({});
  const [ivrSettings, handleIvrEditModal] = useState(false);
  const [ringGroupSettings, handleRingGroupEditModal] = useState(false);
  const [queueEditModal, handleQueueEditModal] = useState(false);
  const [conferenceEditModal, handleConferenceEditModal] = useState(false);
  const { price, billingCycle } = getPriceAndCycle(
    productData,
    rowForDelete?.data?.numType
  );
  const deleteNumber = async (
    event: Record<string, any>,
    params: GridRenderCellParams
  ) => {
    if (reportGroupId === params.row.Numberid) {
      event.preventDefault(); // stop link
      // insert action to report here as well as refetch
      const data = {
        call: methodNameEnum.ADD_SPAM_LIKELY,
        apikey: userAtomContents.apikey,
        businessid: userAtomContents.businessid,
        numberid: params.row.Numberid,
        role: role,
        value: 1,
      };
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.ADD_SPAM_LIKELY,
        payload: data,
      });
      setDeleteStatus(loadableEnum.LOADING);
      refetchBusinessNumbers();
      if (response?.data?.success) {
        enqueueSnackbarWithStyles({
          message: `Successfully Reported Number - ${params.row.DID}`,
          alertProps: { severity: 'success' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: `Encountered an error reporting the number - ${params.row.DID}`,
          alertProps: { severity: 'warning' },
        });
      }
      setDeleteStatus(loadableEnum.HAS_VALUE);
      setDeleteGroupId(undefined);
      setReportGroupId(undefined);
    } else if (deleteGroupId === params.row.Numberid) {
      event.preventDefault(); // stop link
      const data = {
        call: methodNameEnum.DELETE_NUMBER,
        role: role,
        apikey: userAtomContents.apikey,
        businessid: userAtomContents.businessid,
        numberid: params.row.Numberid,
      };
      setDeleteStatus(loadableEnum.LOADING);
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.DELETE_NUMBER,
        payload: data,
      });
      refetchBusinessNumbers();
      if (response?.data?.success) {
        enqueueSnackbarWithStyles({
          message: `Successfully Deleted Number - ${params.row.DID}`,
          alertProps: { severity: 'success' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: `Could not delete this number.`,
          alertProps: { severity: 'error' },
        });
      }
      setDeleteStatus(loadableEnum.HAS_VALUE);
      setDeleteGroupId(undefined);
      setReportGroupId(undefined);
    }
    setShowConfirmDelete(false);
  };

  function useColumns({
    handleIvrEditModal,
    setRowForEdit,
    handleConferenceEditModal,
    handleRingGroupEditModal,
    handleQueueEditModal,
    handleEditModal,
    handleCallModal,
    handleReplaceNumberModal,
    setShowConfirmDelete,
    setRowForDelete,
    setDeleteGroupId,
    setReportGroupId,
    role,
    businessSMSRulesCashed,
    canAddPhoneNumbers,
  }: {
    handleIvrEditModal: (value: boolean) => void;
    setRowForEdit: (value: Record<string, any>) => void;
    handleConferenceEditModal: (value: boolean) => void;
    handleRingGroupEditModal: (value: boolean) => void;
    handleQueueEditModal: (value: boolean) => void;
    handleEditModal: (value: boolean) => void;
    handleCallModal: (value: boolean) => void;
    handleReplaceNumberModal: (value: boolean) => void;
    setShowConfirmDelete: (value: boolean) => void;
    setRowForDelete: (params: GridRenderCellParams) => void;
    setDeleteGroupId: Dispatch<SetStateAction<string | undefined>>;
    setReportGroupId: Dispatch<React.SetStateAction<string | undefined>>;
    role: Role;
    businessSMSRulesCashed: React.MutableRefObject<
      | {
          nameLookup: Record<string, IBusinessSMS>;
          smsIdLookup: Record<string, string>;
        }
      | undefined
    >;
    canAddPhoneNumbers: boolean;
  }) {
    const classes = useStyles();
    const businessSmsEnabled = process.env.VITE_APP_BUSINESS_SMS === '1';

    const [numberId, setNumberId] = useState(null);

    function handleClose() {
      setNumberId(null);
    }

    function handleIvrEdit(row: GridRowData) {
      handleIvrEditModal(true);
      setRowForEdit(row);
    }

    function handleConferenceEdit(row: GridRowData) {
      handleConferenceEditModal(true);
      setRowForEdit(row);
    }

    function handleRingGroupEdit(row: GridRowData) {
      handleRingGroupEditModal(true);
      setRowForEdit(row);
    }

    function handleQueueEdit(row: GridRowData) {
      handleQueueEditModal(true);
      setRowForEdit(row);
    }

    function handleEdit(row: GridRowData) {
      handleEditModal(true);
      setRowForEdit(row);
    }

    function handleCall(row: GridRowData) {
      handleCallModal(true);
      setRowForEdit(row);
    }

    function handleReplaceNumber(row: GridRowData) {
      handleReplaceNumberModal(true);
      setRowForEdit(row);
      // setRefetchToggleForNumbers({});
    }

    const formatter = useCallback(
      (params: GridRenderCellParams) => {
        return (
          <div className={classes.actionButton}>
            <Tooltip title="Edit">
              <IconButton
                name="edit"
                onClick={() => {
                  handleEdit(params.row);
                }}
                size="large"
              >
                <CreateIcon color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                aria-label="delete"
                onClick={() => {
                  setShowConfirmDelete(true);
                  setRowForDelete(params);
                  setDeleteGroupId(params.row.Numberid);
                }}
                size="large"
              >
                <DeleteIcon className={classes.deleteIconButton} />
              </IconButton>
            </Tooltip>
            {role === Role.SUPERADMIN && (
              <PopupState variant="popover">
                {(popupState) => (
                  <>
                    <Tooltip title="Superadmin">
                      <IconButton
                        className={clsx('superStyle', classes.actionsDropdown)}
                        // onClick={() => {
                        //   setNumberId(params.data.Numberid);
                        // }}
                        {...bindTrigger(popupState)}
                        size="large"
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu {...bindMenu(popupState)} disableScrollLock={true}>
                      <MenuItem
                        aria-label="report"
                        className={`superStyle ${classes.dropdownButton}`}
                        onClick={() => {
                          setReportGroupId(params.row.Numberid);
                          popupState.close();
                        }}
                      >
                        <ReportLogo className={classes.deleteIconButton} />
                        <small style={{ marginLeft: 7 }}>Mark as spam</small>
                      </MenuItem>
                      <MenuItem
                        aria-label="call"
                        className={`superStyle ${classes.dropdownButton}`}
                        onClick={() => {
                          handleCall(params.row);
                          popupState.close();
                        }}
                      >
                        <PhoneCallbackIcon />
                        <small style={{ marginLeft: 7 }}>Call me</small>
                      </MenuItem>
                      {process.env.VITE_APP_REPLACE_NUMBER === '1' && (
                        <MenuItem
                          className={`superStyle ${classes.dropdownButton}`}
                          onClick={() => {
                            handleReplaceNumber(params.row);
                            popupState.close();
                          }}
                        >
                          <SwitchIcon />
                          <small style={{ marginLeft: 7 }}>
                            Replace number
                          </small>
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </PopupState>
            )}
          </div>
        );
      },
      [
        handleEdit,
        setShowConfirmDelete,
        setRowForDelete,
        setDeleteGroupId,
        classes,
        role,
        // setNumberId,
        numberId,
        handleClose,
      ]
    );

    const phoneNumberFormatter = useCallback(
      (params: GridRenderCellParams) => {
        return (
          <Tooltip title="Edit Number">
            <NavLink
              onClick={() => {
                handleEdit(params.row);
                trackEditNumber();
              }}
              to="#"
            >
              {params.value}
            </NavLink>
          </Tooltip>
        );
      },
      [handleEdit]
    );

    const columns: IColDef[] = useMemo(
      () => [
        {
          field: 'id',
          headerName: 'ID',
          // headerClassName: dataTableScss.fullWidth,
          // cellClassName: classes.typeColumn,
        },
        {
          field: 'type',
          headerName: 'Type',
          // headerClassName: dataTableScss.fullWidth,
          cellClassName: classes.typeColumn,
        },
        {
          field: 'businessSMSName',
          headerName: 'Team SMS',
          headerClassName: dataTableScss.fullWidth,
          cellClassName: dataTableScss.fullWidth,
          hide: !businessSmsEnabled,
          enableSorting: false,
          renderCell: (params) => (
            <>
              {params.row.businessSMSName !== '' && (
                <Tooltip title="Edit Team SMS">
                  <NavLink
                    to={`/manage/numbers/business-sms/${
                      businessSMSRulesCashed?.current?.nameLookup?.[
                        params.row.businessSMSName
                      ]?.businessSMSId
                    }/edit`}
                  >
                    <div>{params.row.businessSMSName}</div>
                  </NavLink>
                </Tooltip>
              )}
            </>
          ),
        },
        {
          field: 'DID',
          headerName: 'Phone Number',
          headerClassName: dataTableScss.fullWidth,
          cellClassName: dataTableScss.fullWidth,
          renderCell: phoneNumberFormatter,
        },
        {
          field: 'routingRules',
          headerName: 'Routing Rules',
          headerClassName: dataTableScss.fullWidth,
          cellClassName: dataTableScss.fullWidth,
          enableSorting: false,
          renderCell: (params) => {
            return params.row.routingRules ? (
              <NavLink
                to={`/manage/numbers/routing?filter=${params.row.DID}&type=${params.row.type}`}
              >
                <div>{`${params.row.routingRules} ${
                  params.row.routingRules === 1 ? 'Rule' : 'Rules'
                }`}</div>
              </NavLink>
            ) : (
              <div>{'-'}</div>
            );
          },
        },
        {
          field: 'numberName',
          headerName: 'Name',
          headerClassName: dataTableScss.fullWidth,
          cellClassName: dataTableScss.fullWidth,
          renderCell: (params) => {
            return (
              <Tooltip title="Edit Name">
                <NavLink onClick={() => handleEdit(params.row)} to="#">
                  <div>{params.row.numberName ?? ''}</div>
                </NavLink>
              </Tooltip>
            );
          },
        },
        {
          field: 'destinationName',
          headerName: 'Destination Name',
          headerClassName: dataTableScss.fullWidth,
          cellClassName: dataTableScss.fullWidth,
          renderCell: (params) => {
            switch (params.row.type) {
              case 'IVR':
                return (
                  <NavLink onClick={() => handleIvrEdit(params.row)} to="#">
                    <p>{params.value}</p>
                  </NavLink>
                );
              case 'Group':
                return (
                  <NavLink
                    onClick={() => handleRingGroupEdit(params.row)}
                    to="#"
                  >
                    <p>{params.value}</p>
                  </NavLink>
                );
              case 'Agent':
                return (
                  <NavLink
                    to={`/manage/agents/${params.row.destinationid}/agent-reporting`}
                  >
                    <p>{params.value}</p>
                  </NavLink>
                );
              case 'Queue':
                return (
                  <NavLink onClick={() => handleQueueEdit(params.row)} to="#">
                    <p>{params.value}</p>
                  </NavLink>
                );
              case 'Conference':
                return (
                  <NavLink
                    onClick={() => handleConferenceEdit(params.row)}
                    to="#"
                  >
                    <p>{params.value}</p>
                  </NavLink>
                );
              case 'unallocated':
                return <div>{params.value}</div>;
              case 'Boost Set':
                return <div>{params.value}</div>;
              case 'Directory':
                return <div>{params.value}</div>;
              default:
                return <span></span>;
            }
          },
        },
        {
          field: 'defaultuserName',
          headerName: 'Default User',
          headerClassName: dataTableScss.fullWidth,
          cellClassName: dataTableScss.fullWidth,
          renderCell: (params) =>
            params.value === 'N/A' ? (
              <span>{params.value}</span>
            ) : (
              <NavLink
                to={`/manage/agents/${params.row.defaultuser}/agent-reporting`}
              >
                <p>{params.value}</p>
              </NavLink>
            ),
        },
        {
          field: 'portStatus',
          headerName: 'Status',
          // headerClassName: dataTableScss.fullWidth,
          // cellClassName: dataTableScss.fullWidth,
        },
        {
          field: 'verizonImageDataStatus',
          headerName: 'Verizon status',
          headerClassName: clsx('superStyle', classes.typeColumn),
          hide: role !== Role.SUPERADMIN,
          disableExport: true,
          cellClassName: classes.typeColumn,
          renderCell: (params) => (
            <span>
              {params.value}
              <PopoverGeneric
                type="MouseOver"
                showPopover={params.value !== ''}
                showPopoverIcon={true}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                popoverData={
                  <div style={{ maxHeight: '400px', maxWidth: '180px' }}>
                    <img
                      style={{ maxHeight: '100%', maxWidth: '100%' }}
                      src={params.row.verizonImageDataObject?.image}
                      alt={params.row.verizonImageDataObject?.image}
                    />
                  </div>
                }
              />
            </span>
          ),
        },
        {
          field: 'tmobileImageDataStatus',
          headerName: 'Tmobile Status',
          headerClassName: clsx('superStyle', classes.typeColumn),
          hide: role !== Role.SUPERADMIN,
          disableExport: true,
          cellClassName: classes.typeColumn,
          renderCell: (params) => (
            <span>
              {params.value}
              <PopoverGeneric
                type="MouseOver"
                showPopover={params.value !== ''}
                showPopoverIcon={true}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                popoverData={
                  <div style={{ maxHeight: '400px', maxWidth: '180px' }}>
                    <img
                      style={{ maxHeight: '100%', maxWidth: '100%' }}
                      src={params.row.tmobileImageDataObject?.image}
                      alt={params.row.tmobileImageDataObject?.image}
                    />
                  </div>
                }
              />
            </span>
          ),
        },
        {
          field: 'attImageDataStatus',
          headerName: 'ATT Status',
          headerClassName: clsx('superStyle', classes.typeColumn),
          hide: role !== Role.SUPERADMIN,
          disableExport: true,
          cellClassName: classes.typeColumn,
          renderCell: (params) => (
            <span>
              {params.value}
              <PopoverGeneric
                type="MouseOver"
                showPopover={params.value !== ''}
                showPopoverIcon={true}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                popoverData={
                  <div style={{ maxHeight: '400px', maxWidth: '180px' }}>
                    <img
                      style={{ maxHeight: '100%', maxWidth: '100%' }}
                      src={params.row.attImageDataObject?.image}
                      alt={params.row.attImageDataObject?.image}
                    />
                  </div>
                }
              />
            </span>
          ),
        },
        {
          field: 'IPQS_fraud_score',
          headerName: 'IPQS Fraud Score ⓘ',
          // headerClassName: dataTableScss.fullWidth,
          headerClassName: 'superStyle',
          // cellClassName: dataTableScss.fullWidth,
          hide: role !== Role.SUPERADMIN,
          disableExport: true,
          Header: () => (
            <Tooltip
              title={
                'IPQualityScore.com: Fraud scores of 85 are high risk numbers that are likely to engage in malicious behavior.'
              }
            >
              <span>IPQS Fraud Score ⓘ</span>
            </Tooltip>
          ),
        },
        {
          field: 'ScamLikelyManual',
          headerName: 'Scam Likely',
          // headerClassName: dataTableScss.fullWidth,
          headerClassName: 'superStyle',
          // cellClassName: dataTableScss.fullWidth,
          renderCell: (params) => {
            return params.value === 1 ? <span>Yes</span> : <span>No</span>;
          },
          hide: role !== Role.SUPERADMIN,
          disableExport: true,
        },
        {
          field: 'actions',
          headerName: 'Actions\xa0\xa0\xa0\xa0\xa0\xa0',
          width: 200,
          headerAlign: 'center',
          cellClassName: (classes.dropdownOverflow, dataTableScss.center),
          disableExport: true,
          enableSorting: false,
          renderCell: (params) => {
            return formatter(params);
          },
          hide: !canAddPhoneNumbers,
        },
      ],
      [
        handleIvrEditModal,
        setRowForEdit,
        handleConferenceEditModal,
        handleRingGroupEditModal,
        handleQueueEditModal,
        handleEditModal,
        handleCallModal,
        handleReplaceNumberModal,
        setShowConfirmDelete,
        setRowForDelete,
        setDeleteGroupId,
        setReportGroupId,
        role,
        businessSMSRulesCashed,
        canAddPhoneNumbers,
      ]
    );

    return columns;
  }

  const columns = useColumns({
    handleIvrEditModal,
    setRowForEdit,
    handleConferenceEditModal,
    handleRingGroupEditModal,
    handleQueueEditModal,
    handleEditModal,
    handleCallModal,
    handleReplaceNumberModal,
    setShowConfirmDelete,
    setRowForDelete,
    setDeleteGroupId,
    setReportGroupId,
    role,
    businessSMSRulesCashed,
    canAddPhoneNumbers,
  });

  const { callback: setSearchDebounce } = useDebouncedCallback(
    (value: string) => {
      // add wildcard
      setSearch(value ? '%' + value + '%' : '');
      resetView();
    },
    250 // debounce timeout in milliseconds
  );

  const handleChangeSearch = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchDebounce(event.target.value ?? '');
  };

  const textFieldRef = useRef<HTMLInputElement>(null);

  const handleCloseSearch = (e: string) => {
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.value = 'boost set ' + e;
        textFieldRef.current.focus();
      }
      setSearch('boost set ' + e);
      resetView();
    }, 250);
    handleBoostSetModal(false);
  };
  const handleCloseNoSearch = () => {
    handleBoostSetModal(false);
    refetch();
  };

  function useToolbar({
    role,
    handleNewModal,
    handleDeleteModal,
    selectedLength,
    search,
    textFieldRef,
    handleChangeSearch,
    spamLikelyScoreResult,
    handleBoostSetModal,
    refetch,
    handlePortModal,
    handleExtModal,
    setIsDirectoryNumber,
  }: {
    role: Role;
    handleNewModal: (value: boolean) => void;
    handleDeleteModal: Dispatch<SetStateAction<boolean>>;
    selectedLength: number | undefined;
    search: string;
    textFieldRef: React.RefObject<HTMLInputElement>;
    handleChangeSearch:
      | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
      | undefined;
    handleBoostSetModal: (value: boolean) => void;
    refetch: () => void;
    spamLikelyScoreResult:
      | {
          WeightedSpamScore: number;
          AttSpamCount: number;
          TotalCount: number;
          CombinedSpamCount: number;
          SpamPercentage: number;
          VerizonSpamCount: number;
          TmobileSpamCount: number;
        }
      | undefined;
    handlePortModal: (value: boolean) => void;
    handleExtModal: (value: boolean) => void;
    setIsDirectoryNumber: (value: boolean) => void;
  }) {
    const classes = useStyles();

    const numberHealth = {
      greatNumberScore: 'Great',
      goodNumberScore: 'Good',
      averageNumberScore: 'Fair',
      poorNumberScore: 'Poor',
    };

    const spamScoreCategory = useMemo(() => {
      const weightedSpamScoreText =
        spamLikelyScoreResult?.WeightedSpamScore ?? (('-' as any) as number);
      if (weightedSpamScoreText >= 88.6) {
        return 'greatNumberScore';
      } else if (weightedSpamScoreText >= 78) {
        return 'goodNumberScore';
      } else if (weightedSpamScoreText >= 65.6) {
        return 'averageNumberScore';
      } else if (weightedSpamScoreText > 0) {
        return 'poorNumberScore';
      } else {
        return 'cautionNumberScore';
      }
    }, [spamLikelyScoreResult?.WeightedSpamScore]);

    const toolbarComponent = useCallback(
      (props) => {
        return (
          <div>
            {role === Role.SUPERADMIN &&
              process.env.VITE_APP_NUMBER_SCORE === '1' && (
                <div className={'superStyle'}>
                  <div
                    style={{
                      height: '80px',
                      padding: '16px 16px',
                      display: 'flex',
                      flexWrap: 'nowrap',
                      justifyContent: 'space-between',
                      maxWidth: '1020px',
                    }}
                  >
                    <div className={classes.statHeaderContainer}>
                      <div
                        className={clsx(
                          classes.statHeader,
                          classes.statHeaderCenter
                        )}
                      >
                        Number Health
                      </div>
                      <div
                        className={clsx(
                          classes.statHeaderCenter,
                          classes.numberScoreContainer,
                          classes.numberScore,
                          classes[spamScoreCategory]
                        )}
                      >
                        {numberHealth?.[spamScoreCategory]
                          ? numberHealth[spamScoreCategory]
                          : '-'}
                      </div>
                      <Tooltip title="Number Health indicates how likely your numbers are to not show up as spam when making outgoing calls. It is measured by testing outbound dials and checking their spam status with the three large US carriers as well as taking into account market share.">
                        <InfoIcon
                          style={{
                            alignSelf: 'center',
                            cursor: 'pointer',
                            color: 'grey',
                          }}
                        />
                      </Tooltip>
                    </div>
                    <div />
                    <div className={classes.statHeaderContainer2}>
                      <div
                        className={clsx(
                          classes.statHeader,
                          classes.statHeaderCenter
                        )}
                      >
                        Numbers
                      </div>
                      <div
                        className={clsx(
                          classes.statHeaderCenter,
                          classes.numberScoreContainer,
                          classes.numberScore2
                        )}
                      >
                        {spamLikelyScoreResult?.TotalCount ?? '-'}
                      </div>
                    </div>
                    <div className={classes.statHeaderContainer2}>
                      <div
                        className={clsx(
                          classes.statHeader,
                          classes.statHeaderCenter
                        )}
                      >
                        Spam
                      </div>
                      <div
                        className={clsx(
                          classes.statHeaderCenter,
                          classes.numberScoreContainer,
                          classes.numberScore2
                        )}
                      >
                        {spamLikelyScoreResult?.CombinedSpamCount ?? '-'}
                      </div>
                    </div>
                    <div className={classes.statHeaderContainer2}>
                      <div
                        className={clsx(
                          classes.statHeader,
                          classes.statHeaderCenter
                        )}
                      >
                        Spam %
                      </div>
                      <div
                        className={clsx(
                          classes.statHeaderCenter,
                          classes.numberScoreContainer,
                          classes.numberScore2
                        )}
                      >
                        {spamLikelyScoreResult?.SpamPercentage ?? '-'}
                      </div>
                    </div>
                    <div className={classes.statHeaderContainer2}>
                      <div
                        className={clsx(
                          classes.statHeader,
                          classes.statHeaderCenter
                        )}
                      >
                        Verizon
                      </div>
                      <div
                        className={clsx(
                          classes.statHeaderCenter,
                          classes.numberScoreContainer,
                          classes.numberScore2
                        )}
                      >
                        {spamLikelyScoreResult?.VerizonSpamCount ?? '-'}
                      </div>
                    </div>
                    <div className={classes.statHeaderContainer2}>
                      <div
                        className={clsx(
                          classes.statHeader,
                          classes.statHeaderCenter
                        )}
                      >
                        Tmobile
                      </div>
                      <div
                        className={clsx(
                          classes.statHeaderCenter,
                          classes.numberScoreContainer,
                          classes.numberScore2
                        )}
                      >
                        {spamLikelyScoreResult?.TmobileSpamCount ?? '-'}
                      </div>
                    </div>
                    <div className={classes.statHeaderContainer2}>
                      <div
                        className={clsx(
                          classes.statHeader,
                          classes.statHeaderCenter
                        )}
                      >
                        Att
                      </div>
                      <div
                        className={clsx(
                          classes.statHeaderCenter,
                          classes.numberScoreContainer,
                          classes.numberScore2
                        )}
                      >
                        {spamLikelyScoreResult?.AttSpamCount ?? '-'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            <EnhancedTableToolbar
              {...props}
              dataType="Numbers"
              numSelected={selectedLength}
              csvExport={isCsvExportEnabled}
              onCsvExport={(dataType, start, end, csvData) => {
                setCsvProps({
                  dataType: dataType,
                  start: start,
                  end: end,
                  csvData: csvData,
                });
                setShowExportModal(true);
              }}
              hideCsvExportButton={!isCsvExportEnabled}
              customSearchField={
                <TextField
                  inputRef={textFieldRef}
                  variant="outlined"
                  className={classes.search}
                  id="searchBox"
                  inputProps={{
                    'data-pup': 'search-bar-input',
                  }}
                  size="small"
                  color="primary"
                  defaultValue={search}
                  label="Search"
                  type="search"
                  onChange={handleChangeSearch}
                  data-pup="table-search"
                />
              }
              rightComponent={
                <div
                  style={{
                    height: '39px',
                    marginLeft: 'auto',
                    display: 'flex',
                    flexWrap: 'nowrap',
                  }}
                >
                  {role === Role.SUPERADMIN && (
                    <>
                      <Button
                        className={classes.button + ' superStyle'}
                        color="primary"
                        variant="contained"
                        disableElevation={true}
                        disabled={props.isLoading}
                        onClick={() => {
                          trackAddBoostSetMGMT();
                          handleBoostSetModal(true);
                          refetch();
                        }}
                      >
                        Boost Set MGMT
                      </Button>
                      <Button
                        className={classes.button + ' superStyle'}
                        color="primary"
                        variant="contained"
                        disableElevation={true}
                        disabled={props.isLoading}
                        onClick={() => {
                          trackAddPort();
                          handlePortModal(true);
                        }}
                      >
                        Add Port
                      </Button>
                      <Button
                        className={classes.button + ' superStyle'}
                        color="primary"
                        variant="contained"
                        disableElevation={true}
                        disabled={props.isLoading}
                        onClick={() => {
                          trackAddExternalNumber();
                          handleExtModal(true);
                        }}
                      >
                        Add External Number
                      </Button>
                      <Button
                        className={classes.button + ' superStyle'}
                        color="primary"
                        variant="contained"
                        disableElevation={true}
                        disabled={props.isLoading}
                        onClick={() => {
                          trackAddDirectoryExternalNumber();
                          setIsDirectoryNumber(true);
                          handleExtModal(true);
                        }}
                      >
                        Add Directory External Number
                      </Button>
                    </>
                  )}
                  <ToolbarAddDeleteButton
                    className="numbersAdd"
                    numSelected={selectedLength}
                    onClickForAdd={() => {
                      trackAddNumber();
                      handleNewModal(true);
                    }}
                    onClickForDelete={() => {
                      handleDeleteModal(true);
                    }}
                    isLoading={props.isLoading}
                  />
                </div>
              }
            />
          </div>
        );
      },
      [
        role,
        handleNewModal,
        handleDeleteModal,
        selectedLength,
        search,
        textFieldRef.current,
        handleChangeSearch,
        spamLikelyScoreResult,
        handleBoostSetModal,
        refetch,
        handlePortModal,
        handleExtModal,
        setIsDirectoryNumber,
      ]
    );

    return toolbarComponent;
  }

  const ToolbarComponent = useToolbar({
    role,
    handleNewModal,
    handleDeleteModal,
    selectedLength: selected?.length,
    search,
    textFieldRef,
    handleChangeSearch,
    spamLikelyScoreResult:
      numbersSpamLikelyScore.state === 'hasValue'
        ? numbersSpamLikelyScore.contents?.response?.data?.result?.[0]
        : undefined,
    handleBoostSetModal,
    refetch,
    handlePortModal,
    handleExtModal,
    setIsDirectoryNumber,
  });

  const filteredColumns = useMemo(
    () => columns.filter((column) => !column.hide),
    [columns]
  );

  // Cache the count to persist across page changes
  const prevCount = useRef<number | undefined>(businessNumbers?.count);
  useEffect(() => {
    if (businessNumbers?.count) {
      prevCount.current = businessNumbers?.count;
    }
  }, [businessNumbers?.count]);

  return (
    <Paper elevation={0} square={true}>
      {/* <DashboardTourExtraSpotlights /> */}
      <DataTable
        className="numbersTable"
        enableSelectAll={true}
        setSelected={setSelected} // hide selection
        rows={rowsCached?.current ?? []}
        // searchText={filterForBoost ? search : undefined}
        loading={!isBusinessNumbersSuccess || isBusinessNumbersFetching}
        columns={filteredColumns}
        ToolbarComponent={ToolbarComponent}
        sortModel={sortModel}
        onPageSizeChange={handlePageSizeChange}
        onSortModelChange={setGridSortModel}
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        selectedToggle={selectedToggle}
        setSelectedToggle={setSelectedToggle}
        rowCount={businessNumbers?.count ?? prevCount.current}
        onPageChange={handlePageChange}
        pagination
      />
      <HistoryExportModal
        type="kixie-numbers"
        show={showExportModal}
        page={page}
        pageSize={pageSize}
        totalRows={businessNumbers?.count ?? prevCount.current ?? 0}
        csvProps={csvProps}
        rows={rowsCached?.current ?? []}
        filters={{ search }}
        handleClose={() => {
          setShowExportModal(false);
        }}
      />
      <BoostSetMgmtModal
        show={!!boostSetModal}
        handleCloseSearch={handleCloseSearch}
        handleCloseNoSearch={handleCloseNoSearch}
        refetchBusinessNumbers={refetchBusinessNumbers}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        handleBoostSetModal={handleBoostSetModal}
        boostSetPricing={boostSetPricing}
        trancheRes={trancheRes}
        isLoading={isLoading || isFetching}
      />
      <AddNumberModal
        show={!!newModal}
        handleClose={handleNewModal}
        refetchBusinessNumbers={refetchBusinessNumbers}
        businessSMSRulesLoadable={businessSMSRulesLoadable}
        setRefetchToggleForSMSRules={setRefetchToggleForSMSRules}
        productData={productData}
      />
      <AddPortModal
        show={!!portModal}
        handleClose={handlePortModal}
        refetchBusinessNumbers={refetchBusinessNumbers}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        methodNameEnum={methodNameEnum}
      />
      <AddExtModal
        show={!!extModal}
        handleClose={() => {
          setIsDirectoryNumber(false);
          handleExtModal(false);
        }}
        refetchBusinessNumbers={refetchBusinessNumbers}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        methodNameEnum={methodNameEnum}
        isDirectoryNumber={isDirectoryNumber}
      />
      <DeleteNumberModal
        refetchBusinessNumbers={refetchBusinessNumbers}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        selected={selected}
        setSelectedToggle={setSelectedToggle}
        show={!!deleteModal}
        userAtom={userAtomLoadable}
        mutation={mutation}
        handleClose={() => handleDeleteModal(false)}
      />
      <EditNumberModal
        show={!!editModal}
        handleClose={handleEditModal}
        methodNameEnum={methodNameEnum}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        number={rowForEdit}
        refetchBusinessNumbers={refetchBusinessNumbers}
        businessSMSRulesLoadable={businessSMSRulesLoadable}
      />
      <MakePhoneCallModal
        show={!!makePhoneCallModal}
        handleClose={handleCallModal}
        fromPhoneNumber={rowForEdit.DID}
        businessId={userAtomContents?.businessid}
      />
      <ReplaceNumberModal
        number={rowForEdit}
        show={!!replaceNumberModal}
        fromPhoneNumber={rowForEdit.DID}
        handleClose={() => {
          handleReplaceNumberModal(false);
          refetchBusinessNumbers();
        }}
      />
      <IvrSettings
        show={!!ivrSettings}
        id={rowForEdit.destinationid}
        onHide={() => {
          refetchBusinessNumbers();
          handleIvrEditModal(false);
        }}
      />
      <RingGroupSettings
        show={!!ringGroupSettings}
        groupid={rowForEdit.destinationid}
        onHide={() => {
          refetchBusinessNumbers();
          handleRingGroupEditModal(false);
        }}
      />
      <QueueEditDialog
        show={!!queueEditModal}
        id={rowForEdit.destinationid}
        onHide={() => {
          refetchBusinessNumbers();
          handleQueueEditModal(false);
        }}
      />
      <ConferenceModal
        show={!!conferenceEditModal}
        id={rowForEdit.destinationid}
        methodNameEnum={methodNameEnum}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        conferences={getBusinessConferences}
        refetchBusinessNumbers={refetchBusinessNumbers}
        handleClose={() => {
          handleConferenceEditModal(false);
        }}
      />
      {showConfirmDelete && (
        <ConfirmDeleteNumberModal
          price={price}
          billingCycle={billingCycle}
          deleteStatus={deleteStatus}
          showConfirmDelete={showConfirmDelete}
          setShowConfirmDelete={setShowConfirmDelete}
          rowForDelete={rowForDelete}
          deleteNumber={deleteNumber}
          userAtomContents={userAtomContents}
          productData={productData}
        />
      )}
    </Paper>
  );
}

interface IBusinessSMS {
  businessSMSId: number;
  name: string;
}
